import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { getApiUrl } from '../apiUrl';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

const FindStoreByLocation = ({ open, onClose, onStoreSelect, mapCenter, onLocationObtained }) => {
  const [loading, setLoading] = useState(false);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [coordsPaste, setCoordsPaste] = useState('');
  const locationRequestedRef = useRef(false);

  const API_URL = getApiUrl();

  useEffect(() => {
    if (!open) {
      locationRequestedRef.current = false;
      return;
    }
    // Prefill from map/troop center when opening (coords are [lng, lat])
    if (mapCenter && Array.isArray(mapCenter) && mapCenter.length === 2) {
      setLngInput(String(mapCenter[0]));
      setLatInput(String(mapCenter[1]));
    }
  }, [open, mapCenter]);

  const parsePaste = (value) => {
    const cleaned = String(value).trim().replace(/^\(|\)$/g, '');
    const parts = cleaned.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    const a = Number(parts[0]);
    const b = Number(parts[1]);
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    // Heuristic: if |first| > 90, treat as lng,lat; else lat,lng (US longitudes are ~ -70 to -125)
    if (Math.abs(a) > 90) return { lng: a, lat: b };
    return { lat: a, lng: b };
  };

  const findStoresNearby = async (lat, lng, radius = 0.5) => {
    setLoading(true);
    setError('');
    setNearbyStores([]);
    try {
      if (onLocationObtained) {
        onLocationObtained([lng, lat]);
      }
      const response = await axios.get(`${API_URL}/api/stores/nearby`, {
        params: { lat, lng, radius: 0.5, limit: 15 },
        timeout: 30000
      });
      setNearbyStores(response.data || []);
      if (!response.data?.length) {
        setError('No stores found near those coordinates. Try a wider area or populate stores for this troop in /admin.');
      }
    } catch (err) {
      console.error('Error finding stores:', err);
      setError('Error finding stores: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchCoords = () => {
    setLocationError('');
    let lat = Number(latInput);
    let lng = Number(lngInput);

    if (coordsPaste.trim()) {
      const parsed = parsePaste(coordsPaste);
      if (!parsed) {
        setError('Could not parse coordinates. Use lat,lng (e.g. 36.6437, -93.2185).');
        return;
      }
      lat = parsed.lat;
      lng = parsed.lng;
      setLatInput(String(lat));
      setLngInput(String(lng));
    }

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setError('Enter valid latitude and longitude.');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('Latitude must be -90…90 and longitude -180…180.');
      return;
    }
    findStoresNearby(lat, lng, 0.5);
  };

  const handleUseTroopCenter = () => {
    if (!mapCenter || mapCenter.length !== 2) {
      setError('No troop/map center available.');
      return;
    }
    const [lng, lat] = mapCenter;
    setLatInput(String(lat));
    setLngInput(String(lng));
    findStoresNearby(lat, lng, 0.5);
  };

  const handleGetLocation = async () => {
    setLoading(true);
    setError('');
    setLocationError('');
    setNearbyStores([]);

    try {
      let latitude;
      let longitude;

      if (Capacitor.isNativePlatform()) {
        let status = await Geolocation.checkPermissions();
        if (status.location !== 'granted') {
          status = await Geolocation.requestPermissions();
        }
        if (status.location !== 'granted') {
          setLocationError('Location permission denied.');
          setLoading(false);
          return;
        }
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } else if ('geolocation' in navigator) {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (err) => {
              setLocationError(err.message || 'Location error');
              reject(err);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        });
      } else {
        setLocationError('Location services are not available in your browser.');
        setLoading(false);
        return;
      }

      setLatInput(String(latitude));
      setLngInput(String(longitude));
      await findStoresNearby(latitude, longitude, 0.5);
    } catch (_err) {
      setLoading(false);
    }
  };

  const handleStoreClick = (store) => {
    onStoreSelect(store);
    onClose();
    setNearbyStores([]);
    setError('');
  };

  const handleClose = () => {
    onClose();
    setNearbyStores([]);
    setError('');
    setLocationError('');
    setCoordsPaste('');
    locationRequestedRef.current = false;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 520,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Share clearance</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter coordinates near the store (temporary until mobile location). We&apos;ll list troop stores within about 0.5 miles.
        </Typography>

        <TextField
          fullWidth
          label="Paste coordinates"
          placeholder="36.6437, -93.2185"
          value={coordsPaste}
          onChange={(e) => setCoordsPaste(e.target.value)}
          sx={{ mb: 1.5 }}
          size="small"
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            fullWidth
            label="Latitude"
            value={latInput}
            onChange={(e) => setLatInput(e.target.value)}
            size="small"
          />
          <TextField
            fullWidth
            label="Longitude"
            value={lngInput}
            onChange={(e) => setLngInput(e.target.value)}
            size="small"
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSearchCoords}
          disabled={loading}
          sx={{ backgroundColor: '#202E40', mb: 1 }}
        >
          Find nearby stores
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleUseTroopCenter}
          disabled={loading || !mapCenter}
          sx={{ mb: 1 }}
        >
          Use troop map center
        </Button>
        <Button
          variant="text"
          fullWidth
          startIcon={<LocationOnIcon />}
          onClick={handleGetLocation}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          Use device location (optional)
        </Button>

        <Divider sx={{ mb: 2 }} />

        {loading && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Finding nearby stores…
            </Typography>
          </Box>
        )}

        {(error || locationError) && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'info.main',
              borderRadius: 2,
              display: 'flex',
              gap: 1.5
            }}
          >
            <InfoIcon sx={{ color: 'info.main' }} />
            <Typography variant="body2">{error || locationError}</Typography>
          </Box>
        )}

        {nearbyStores.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select a nearby store
            </Typography>
            <Box sx={{ maxHeight: '40vh', overflowY: 'auto' }}>
              {nearbyStores.map((store) => (
                <Card
                  key={store._id}
                  sx={{ mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleStoreClick(store)}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {store.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {store.address?.fullAddress || 'Address not available'}
                    </Typography>
                    {store.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {store.phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FindStoreByLocation;
